import {
  ChartColumn,
  ClipboardList,
  Clock,
  FileText,
  House,
  MessageCircle,
  Package,
  PackageSearch,
  RotateCcw,
  Settings,
  ShoppingCart,
  SquareCheck,
  Store,
  Truck,
  Users,
  Wallet,
  Warehouse,
  createLucideIcon,
  type LucideIcon,
} from "lucide-react";
import {
  can,
  canRequirement,
  type EffectivePermissions,
  type PermissionKey,
  type PermissionRequirement,
} from "@till-erp/platform/permissions";

/**
 * Ablauf-Symbol der Automatik (Ticket 1.243, E12; Entwurf `flaeche/B01-startseite.dc.html`): zwei Kästen, verbunden,
 * oben ein Pfeil nach rechts.
 */
export const FlowIcon: LucideIcon = createLucideIcon("till-flow", [
  ["rect", { x: "3", y: "3", width: "7", height: "7", rx: "2", key: "box-a" }],
  [
    "rect",
    { x: "14", y: "14", width: "7", height: "7", rx: "2", key: "box-b" },
  ],
  ["path", { d: "M6.5 10v4a3 3 0 0 0 3 3H14", key: "link" }],
  ["path", { d: "M14 6.5h3.5", key: "arrow-line" }],
  ["path", { d: "m16 4.5 2 2-2 2", key: "arrow-head" }],
]);

/** Story 24.1: Team-Chat (Kanäle, Mitgliedschaft, Nachrichten) – mit dem Recht `chat:team` (AD-40). */
export const CHAT_PATH = "/chat";

/** Story 11.41: Versand › Sendungen (Liste `shipments`). */
export const SHIPMENTS_PATH = "/versand/sendungen";

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

/** Seitenleiste oben – Reihenfolge verbindlich (EXPERIENCE §2.1). */
export const mainNav: readonly NavItem[] = [
  { label: "Start", path: "/", icon: House },
  { label: "Aufgaben", path: "/aufgaben", icon: SquareCheck },
  // Story 24.1: Team-Chat direkt nach „Aufgaben“ (Zusammenarbeit).
  { label: "Chat", path: CHAT_PATH, icon: MessageCircle },
  { label: "Aufträge", path: "/auftraege", icon: ClipboardList },
  { label: "Angebote", path: "/angebote", icon: FileText },
  { label: "Kunden", path: "/kunden", icon: Users },
  { label: "Artikel", path: "/artikel", icon: Package },
  { label: "Lager", path: "/lager", icon: Warehouse },
  { label: "Einkauf", path: "/einkauf", icon: ShoppingCart },
  { label: "Versand", path: "/versand", icon: Truck },
  // Story 11.41: Liste aller Sendungen, direkt hinter „Versand“ (für jede Rolle).
  { label: "Sendungen", path: SHIPMENTS_PATH, icon: PackageSearch },
  { label: "Retouren", path: "/retouren", icon: RotateCcw },
  { label: "Finanzen", path: "/finanzen", icon: Wallet },
  { label: "Berichte", path: "/berichte", icon: ChartColumn },
  // Change 22.10.2026 Zeiterfassung (Story 22.1): letzter Eintrag oben, nach „Berichte“ [RUDOLF 22.10.2026, Eingang §6 E1].
  { label: "Zeiterfassung", path: "/zeiterfassung", icon: Clock },
];

/**
 * Bereiche der Symbolleiste (Change 11.10.2026 Design S3, Entscheidung S4; Story 1.77): fassen die Einträge von
 * `mainNav` in **unveränderter Reihenfolge** zusammen. Ein Bereich mit mehreren Einträgen öffnet in der schmalen Leiste
 * beim Überfahren oder Fokus das Ausklapp-Menü (Abnahme: „Verkauf“ → Aufträge, Angebote, Kunden) und ist in der breiten
 * Leiste aufklappbar; ein Bereich mit genau einem Eintrag ist direkt der Link.
 */
export interface NavArea {
  label: string;
  icon: LucideIcon;
  items: readonly NavItem[];
}

function navItems(...paths: string[]): NavItem[] {
  return paths.map((path) => {
    const item = mainNav.find((i) => i.path === path);
    if (!item) throw new Error(`Navigation: ${path} fehlt in mainNav`);
    return item;
  });
}

export const navAreas: readonly NavArea[] = [
  { label: "Start", icon: House, items: navItems("/") },
  { label: "Aufgaben", icon: SquareCheck, items: navItems("/aufgaben") },
  { label: "Chat", icon: MessageCircle, items: navItems(CHAT_PATH) },
  {
    label: "Verkauf",
    icon: Store,
    items: navItems("/auftraege", "/angebote", "/kunden"),
  },
  { label: "Lager", icon: Warehouse, items: navItems("/artikel", "/lager") },
  { label: "Einkauf", icon: ShoppingCart, items: navItems("/einkauf") },
  { label: "Versand", icon: Truck, items: navItems("/versand", SHIPMENTS_PATH, "/retouren") },
  { label: "Finanzen", icon: Wallet, items: navItems("/finanzen") },
  { label: "Berichte", icon: ChartColumn, items: navItems("/berichte") },
  { label: "Zeiterfassung", icon: Clock, items: navItems("/zeiterfassung") },
];

/** Seitenleiste unten (vor dem Profil); Name und Symbol der Automatik nach Entwurf (Ticket 1.243, E12). */
export const footNav: readonly NavItem[] = [
  { label: "Automatik", path: "/automatisierung", icon: FlowIcon },
  { label: "Einstellungen", path: "/einstellungen", icon: Settings },
];

/**
 * Seitenschutz und Navigation aus den Rechten (Story 28.10, Change „Rollen und Rechte“ §5.3, AD-43): Menü, „+ Neu“,
 * Hülle und Seitenschutz lesen nur die wirksamen Rechte aus `GET /me` (`sessionPermissions`) – keine Rollen-Sonderfassung.
 * Dieselben Rechte wie der Server (`x-permission`, `LIST_PERMISSIONS`, Grundzugang `start:read`). **Die Oberfläche blendet
 * nur aus**; geschützt ist allein durch den Server.
 */

/**
 * Nötiges Recht einer Seite: Liste von Alternativen (eine reicht); jede Alternative ist eine Anforderung nach
 * `canRequirement` (ein Recht oder eine Liste, von der jedes Element nötig ist). `everyone` = jede angemeldete Person.
 */
export type PageRight = readonly PermissionRequirement[] | "everyone";

/** Grundzugang des Büros (wie `OFFICE_BASE` im Server): gilt für jede Seite ohne eigenen Eintrag. */
export const OFFICE_BASE_RIGHT: PermissionKey = "start:read";

const STAFF_ADMIN_TIME: PermissionRequirement = ["time:leave", "start:read"];

/**
 * Seitenrechte (Story 28.10): das längste passende Präfix an einer Segmentgrenze gewinnt; `/` gilt nur genau. Ohne
 * Eintrag braucht eine Seite den Grundzugang `start:read` (wie der Server).
 */
export const PAGE_RIGHTS: Readonly<Record<string, PageRight>> = Object.freeze({
  // Start: Büro-Startseite (Grundzugang) oder die eigene Startseite der Zeiterfassung (Rolle Mitarbeiter, Story 22.11).
  "/": ["start:read", "time:leave"],
  "/profil": "everyone",
  // Story 24.1: Team-Chat (AD-40).
  [CHAT_PATH]: ["chat:team"],
  "/__ci/chat": ["chat:team"],
  "/aufgaben": ["tasks:read"],
  // Klärfälle lesen Aufträge, Kunden und Artikel (Story 23.17).
  "/aufgaben/klaerfaelle": ["start:read"],
  // Lauf ohne Liste im Pfad (Bezug einer Aufgabe, Story 23.17): leitet in die Liste des Laufs weiter.
  "/massenbearbeitungen": ["tasks:read", "start:read"],
  "/auftraege": ["orders:read"],
  "/auftraege/neu": ["orders:write"],
  "/auftraege/rahmenauftraege/neu": ["orders:write"],
  "/angebote": ["orders:read"],
  "/angebote/neu": ["orders:write"],
  "/kunden": ["customers:read"],
  "/kunden/neu": ["customers:write"],
  "/artikel": ["articles:read"],
  "/artikel/neu": ["articles:write"],
  "/lager": ["articles:read"],
  "/einkauf": ["purchasing:read"],
  "/versand": ["shipping:read"],
  // Story 11.41: Sendungen für jede Rolle – Recht `time:own`, das jede Rolle hat (Liste `shipments`, `everyone`).
  [SHIPMENTS_PATH]: ["time:own"],
  // Abholung liest das Auftragsdetail mit Preisen (Liste `pickup_ready`).
  "/versand/abholung": ["orders:read"],
  "/retouren": ["returns:read"],
  "/finanzen": ["documents:read", "payments:read", "receivables:read", "datev:read"],
  "/finanzen/zahlungen": ["payments:read"],
  "/finanzen/belege": ["documents:read"],
  "/belege": ["documents:read"],
  "/finanzen/offene-posten": ["receivables:read"],
  "/finanzen/datev": ["datev:read"],
  "/berichte": ["reports:read"],
  "/berichte/zahlungseingaenge": ["payments:read"],
  "/zeiterfassung": ["time:own"],
  "/zeiterfassung/meine-zeiten": ["time:own"],
  "/zeiterfassung/urlaub": ["time:leave"],
  "/zeiterfassung/kalender": ["time:leave"],
  "/zeiterfassung/team": [STAFF_ADMIN_TIME],
  "/zeiterfassung/genehmigen": [STAFF_ADMIN_TIME],
  "/zeiterfassung/nachtraege": [STAFF_ADMIN_TIME],
  "/zeiterfassung/monatsuebersicht": [STAFF_ADMIN_TIME],
  "/zeiterfassung/arbeitszeitgesetz": [STAFF_ADMIN_TIME],
  // Anlegen, Massenbearbeitung und Import wie die Listen-Arten des Servers (`route-permissions.ts`: bulk/import =
  // Schreibrecht des Bereichs + `data:bulk`, Aufgaben mit Teil-Umfang `tasks`).
  "/einkauf/lieferanten/neu": ["purchasing:write"],
  "/lager/inventuren/neu": ["articles:write"],
  "/aufgaben/massenbearbeitung": [["tasks:write", "data:bulk#tasks"]],
  "/kunden/massenbearbeitung": [["customers:write", "data:bulk"]],
  "/artikel/massenbearbeitung": [["articles:write", "data:bulk"]],
  "/lager/massenbearbeitung": [["articles:write", "data:bulk"]],
  "/lager/lagerplaetze/massenbearbeitung": [["articles:write", "data:bulk"]],
  "/einkauf/lieferanten/massenbearbeitung": [["purchasing:write", "data:bulk"]],
  "/einkauf/lieferantenpreise/massenbearbeitung": [["purchasing:write", "data:bulk"]],
  "/kunden/import": [["customers:write", "data:bulk"]],
  "/artikel/import": [["articles:write", "data:bulk"]],
  "/lager/import": [["articles:write", "data:bulk"]],
  "/einkauf/lieferanten/import": [["purchasing:write", "data:bulk"]],
  "/einkauf/lieferantenpreise/import": [["purchasing:write", "data:bulk"]],
  "/einstellungen": ["start:read"],
  // Story 27.5 ([RUDOLF 02.11.2026] alle gestalten Etiketten): Galerie und Editor.
  "/einstellungen/drucken/etiketten": ["labels:read"],
} satisfies Record<string, PageRight>);

function trimPath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : pathname;
}

/** Nötiges Recht einer Seite (längstes Präfix an einer Segmentgrenze; ohne Eintrag der Grundzugang). */
export function pageRight(pathname: string): PageRight {
  const path = trimPath(pathname);
  if (path === "/") return PAGE_RIGHTS["/"]!;
  let best: string | null = null;
  for (const prefix of Object.keys(PAGE_RIGHTS)) {
    if (prefix === "/") continue;
    if (path !== prefix && !path.startsWith(`${prefix}/`)) continue;
    if (best === null || prefix.length > best.length) best = prefix;
  }
  return best === null ? [OFFICE_BASE_RIGHT] : PAGE_RIGHTS[best]!;
}

/** Darf die angemeldete Person diese Seite öffnen? (Nur Ausblenden; der Server sperrt dieselben Routen.) */
export function pageAllowed(effective: EffectivePermissions, pathname: string): boolean {
  const right = pageRight(pathname);
  return right === "everyone" || right.some((alternative) => canRequirement(effective, alternative));
}

/**
 * Heimseite: die erste erlaubte aus Start (Grundzugang oder eigene Startseite), Versand und den übrigen Einträgen der
 * Seitenleiste in ihrer Reihenfolge (bis Zeiterfassung, die jede Rolle hat). Ohne Rechte das Profil.
 */
export function homePath(effective: EffectivePermissions): string {
  const candidates = ["/", "/versand", ...mainNav.map((i) => i.path).filter((p) => p !== "/" && p !== "/versand")];
  return candidates.find((path) => pageAllowed(effective, path)) ?? "/profil";
}

/** Name der Heimseite („Start“, „Versand“ …) für den Knopf „<Bereich> öffnen“. */
export function homeLabel(effective: EffectivePermissions): string {
  const home = homePath(effective);
  return mainNav.find((i) => i.path === home)?.label ?? "Profil";
}

/**
 * Hülle nach Rechten: Grundzugang → Büro (Suche, KI-Copilot, Systemstatus); sonst mit Aufgaben → Kopfzeile mit „+ Neu“,
 * Stempel-Chip und Glocke; sonst die schlanke Hülle des Packplatzes (ohne Kopfzeile am PC).
 */
export type ShellMode = "office" | "staff" | "packing";
export function shellMode(effective: EffectivePermissions): ShellMode {
  if (can(effective, OFFICE_BASE_RIGHT)) return "office";
  if (can(effective, "tasks:read")) return "staff";
  return "packing";
}

/** Menü, Fuß und „+ Neu“ der angemeldeten Person. */
export interface AccessNav {
  areas: readonly NavArea[];
  foot: readonly NavItem[];
  newMenu: readonly NewMenuItem[];
}

/**
 * Navigation aus den Rechten (Story 28.10): nur erlaubte Einträge (dieselbe Seitenrechte-Tabelle), leere Bereiche
 * entfallen; der Bereich der Heimseite steht oben (Packplatz: Versand vor Chat – wie zuvor). „+ Neu“ nur mit dem
 * Schreibrecht des Ziels.
 */
export function navFor(effective: EffectivePermissions): AccessNav {
  const allowed = (item: NavItem) => pageAllowed(effective, item.path);
  const areas: NavArea[] = [];
  for (const area of navAreas) {
    const items = area.items.filter(allowed);
    if (items.length === area.items.length) areas.push(area);
    else if (items.length > 0) areas.push({ ...area, items });
  }
  const home = homePath(effective);
  const homeIndex = areas.findIndex((a) => a.items.some((i) => i.path === home));
  if (homeIndex > 0) areas.unshift(...areas.splice(homeIndex, 1));
  return {
    areas,
    foot: footNav.filter(allowed),
    newMenu: newMenu.filter((item) => canRequirement(effective, item.right)),
  };
}

/** Eintrag im Menü „+ Neu“; ohne `path` gibt es das Ziel noch nicht („kommt bald“). */
export interface NewMenuItem {
  label: string;
  path?: string;
  /** Suchparameter des Ziels (z. B. `ablage` = Maske „Auftrag anlegen“ mit Ablage für die PDF, Story 9.4). */
  search?: Record<string, string | number>;
  /**
   * Anlege-Chip in der Kopfzeile am PC (Ticket 1.233, DESIGN „Kopfzeile“): sichtbare Beschriftung ohne Zeichen davor,
   * Name für Hilfsmittel, Hauptknopf (dunkelblau) bzw. KI-Funke „✦“ statt „+“. Seit Ticket 1.275 [RUDOLF 25.10.2026] gibt
   * es keine Chips mehr in der Kopfzeile, nur „+ Neu ▾“; `ai` zeigt den Funken ✦ am Menü-Eintrag.
   */
  chip?: { label: string; name: string; primary?: boolean; ai?: boolean };
  /** Story 28.10: nötiges Recht (Schreibrecht des Ziels); ohne Recht fehlt der Eintrag. */
  right: PermissionRequirement;
}

/** „+ Neu“ im Kopf (EXPERIENCE §2.4, Reihenfolge entschieden): Auftrag · Rahmenauftrag · Angebot · Kunde · Artikel · Retoure · Aufgabe · Bestellung. „Auftrag aus PDF“ und „Angebot aus Anfrage“ stehen nicht mehr hier, sondern als Start in den Masken (Rudolf 04.11.2026). */
export const newMenu: readonly NewMenuItem[] = [
  {
    label: "Auftrag",
    path: "/auftraege/neu",
    chip: { label: "Auftrag", name: "Neuer Auftrag", primary: true },
    right: "orders:write",
  },
  // Story 9.24: Rahmenauftrag mit Abrufen (Anlage-Maske; kein Anlege-Chip in der Kopfzeile).
  { label: "Rahmenauftrag", path: "/auftraege/rahmenauftraege/neu", right: "orders:write" },
  // Story 9.7: Maske „Angebot anlegen“ (gleiche Karten wie „Auftrag anlegen“, ohne Zahlart, Ampel und AB).
  {
    label: "Angebot",
    path: "/angebote/neu",
    chip: { label: "Angebot", name: "Neues Angebot" },
    right: "orders:write",
  },
  { label: "Kunde", path: "/kunden/neu", right: "customers:write" },
  { label: "Artikel", path: "/artikel/neu", right: "articles:write" },
  // „Retoure zu welchem Auftrag?“: öffnet die Auswahl auf der Retourenliste; die Maske selbst liegt am Auftrag.
  { label: "Retoure", path: "/retouren", search: { neu: 1 }, right: "returns:write" },
  // 02.11.2026: Formular „Neue Aufgabe“ (seit Story 23.17 auch für die Rolle Mitarbeiter).
  { label: "Aufgabe", path: "/aufgaben", search: { neu: 1 }, right: "tasks:write" },
  { label: "Bestellung", right: "purchasing:write" },
];

/** Pfad der Aufgabenseite (Zähler in der Seitenleiste). */
export const TASKS_PATH = "/aufgaben";
/** Story 23.5: Massenbearbeitung der Aufgabenliste (Status, Priorität, Fällig, Zuständig, Labels) und ihre Läufe. */
export const TASKS_MASS_EDIT_PATH = "/aufgaben/massenbearbeitung";
/** Story 23.5: Reiter „Liste“ der Aufgabenseite (Rücksprung aus der Massenbearbeitung). */
export const TASKS_LIST_PATH = "/aufgaben?ansicht=liste";

/** Aufträge (Liste; Vollansicht unter `/auftraege/<Kennung>`, epic-auftragseingang Story 1). */
export const ORDERS_PATH = "/auftraege";
/** Neuer Auftrag (Story 9.5, „+ Neu › Auftrag“, Suche „Neuer Auftrag“, „Auftrag für diesen Kunden“). */
export const ORDER_NEW_PATH = "/auftraege/neu";
/** Auftragsentwürfe mit der Ablage „Auftrag aus PDF“ (Story 9.1, „+ Neu › Auftrag aus PDF“). */
export const ORDER_DRAFTS_PATH = "/auftraege/entwuerfe";
/** Liste „Wartet auf Ware“: alle Positionen im Rückstand (Story 7.16, Chip in der Auftragsliste). */
export const ORDER_BACKLOG_PATH = "/auftraege/wartet-auf-ware";
/** Rahmenaufträge (Story 9.24, FR-125): Liste, Anlage-Maske und Vollansicht unter `/auftraege/rahmenauftraege/<Kennung>`. */
export const BLANKET_ORDERS_PATH = "/auftraege/rahmenauftraege";
export const BLANKET_ORDER_NEW_PATH = "/auftraege/rahmenauftraege/neu";

/** Angebote (Story 9.7): Liste über den Listen-Baukasten; `?angelegt=<Nummer>` zeigt die Bestätigung nach dem Speichern. */
export const QUOTES_PATH = "/angebote";
/** Neues Angebot (Story 9.7, „+ Angebot“ in der Kopfzeile und auf der Liste). */
export const QUOTE_NEW_PATH = "/angebote/neu";

/** Finanzen (Ticket 1.250, Entwürfe G01–G05): Übersicht und die vier Bereiche. */
export const FINANCE_PATH = "/finanzen";
export const FINANCE_PAYMENTS_PATH = "/finanzen/zahlungen";
export const FINANCE_DOCUMENTS_PATH = "/finanzen/belege";
export const FINANCE_OPEN_ITEMS_PATH = "/finanzen/offene-posten";
export const FINANCE_DATEV_PATH = "/finanzen/datev";

/** Kundenstamm (Liste; Kundenansicht unter `/kunden/<Kennung>`). */
export const CUSTOMERS_PATH = "/kunden";
/** Neuer Kunde (Story 4.20, „+ Neu › Kunde“). */
export const CUSTOMER_NEW_PATH = "/kunden/neu";
/** Massenbearbeitung der Kundenliste (Story 1.21); ohne Auswahl der Verlauf. */
export const CUSTOMERS_MASS_EDIT_PATH = "/kunden/massenbearbeitung";
/** Mehrere ausgewählte Kunden anonymisieren (Story 18.4, Rudolf 11.10.2026; nur Admin). */
export const CUSTOMERS_ANONYMIZE_PATH = "/kunden/anonymisieren";
/** Story 18.10: Kunden › Import/Export (Zahlungsziel, Skonto, Kreditlimit per CSV/Excel). */
export const CUSTOMERS_IMPORT_PATH = "/kunden/import";

/** Artikelstamm (Liste; Artikelansicht unter `/artikel/<Kennung>`). */
export const ARTICLES_PATH = "/artikel";
/** Neuer Artikel (Story 5.8, „+ Neu › Artikel“). */
export const ARTICLE_NEW_PATH = "/artikel/neu";
/** Story 5.9: Artikel › Stammdaten (Hersteller · Eigenschaften · Kategorien). */
export const CATALOG_MASTER_DATA_PATH = "/artikel/stammdaten";
/** Massenbearbeitung der Artikelliste (Story 5.12); ohne Auswahl der Verlauf. */
export const ARTICLES_MASS_EDIT_PATH = "/artikel/massenbearbeitung";
/** Story 18.9: Artikel › Import/Export (Preise und Stammdaten per CSV/Excel). */
export const ARTICLE_IMPORT_PATH = "/artikel/import";
/** Story 25.2: Artikel › EAN-Nummern (Kacheln Frei/Vergeben/Fremd und Chargen; Liste ab 25.6). */
export const GTIN_NUMBERS_PATH = "/artikel/ean-nummern";
/** Story 25.2: gekaufte EAN-Nummern einlesen. */
export const GTIN_IMPORT_PATH = "/artikel/ean-nummern/einlesen";

/** Lager (Bestand, Lagerplätze, Bestandsbewegungen; Story 5.3). */
export const INVENTORY_PATH = "/lager";
/** Massenbearbeitung der Lagerliste (Story 5.37): Lagerplatz und Mindestbestand. */
export const INVENTORY_MASS_EDIT_PATH = "/lager/massenbearbeitung";
/** Inventuren (Story 5.40): Liste unter „Lager“ (Reiter), neue Inventur und Zählliste je Inventur. */
export const STOCKTAKES_PATH = "/lager/inventuren";
export const STOCKTAKE_NEW_PATH = "/lager/inventuren/neu";
/** Bestandsbewegungen (Story 5.38); `?artikel=<Artikelnummer>` belegt den Filter vor. */
export const INVENTORY_MOVEMENTS_PATH = "/lager/bewegungen";
/** Lagerwert zum Stichtag (Story 5.44); `?stichtag=JJJJ-MM-TT` (ohne Angabe heute). */
export const INVENTORY_VALUATION_PATH = "/lager/lagerwert";
/** Nachzählen (Story 5.48): offene Nachzähl-Markierungen, „Gezählt“ in der geöffneten Zeile. */
export const RECOUNT_PATH = "/lager/nachzaehlen";
/** Lagerplätze (Story 5.39). */
export const BIN_LOCATIONS_PATH = "/lager/lagerplaetze";
/** Massenbearbeitung der Lagerplätze (Story 5.39): Bereich und gesperrt. */
export const BIN_LOCATIONS_MASS_EDIT_PATH =
  "/lager/lagerplaetze/massenbearbeitung";
/** Story 18.10: Lager › Import/Export (Artikel vorhandenen Lagerplätzen zuordnen, per CSV/Excel). */
export const INVENTORY_IMPORT_PATH = "/lager/import";

/** Einkauf (Story 10.1): `/einkauf` führt zu den Bestellvorschlägen (Ticket 33); Lieferanten unter `/einkauf/lieferanten` (Lieferant unter `/einkauf/lieferanten/<Kennung>`). */
export const PURCHASING_PATH = "/einkauf";
export const SUPPLIERS_PATH = "/einkauf/lieferanten";
/** Einkauf › Lieferant anlegen (Ticket 1.216): eigene Seite statt Karte neben der Liste; Kopf-Knopf „+ Lieferant“ (nicht im „+ Neu“-Menü). */
export const SUPPLIER_NEW_PATH = "/einkauf/lieferanten/neu";
/** Story 10.15 (10.1b): Massenbearbeitung der Lieferantenliste (Name, Standardwährung) und ihre Läufe. */
export const SUPPLIERS_MASS_EDIT_PATH =
  "/einkauf/lieferanten/massenbearbeitung";
/** Story 18.17: Einkauf › Lieferanten › Import/Export (Lieferantenstamm; Läufe unter den Lieferantenpreisen). */
export const SUPPLIERS_IMPORT_PATH = "/einkauf/lieferanten/import";
/** Einkauf › Bestellungen (Story 10.3): Lieferantenbestellungen (Datensatz unter `/einkauf/bestellungen/<Kennung>`). */
export const PURCHASE_ORDERS_PATH = "/einkauf/bestellungen";
/** Einkauf › Bestellvorschläge (Story 10.6): offene Vorschläge des täglichen Laufs, gruppiert nach Lieferant. */
export const REORDER_PROPOSALS_PATH = "/einkauf/bestellvorschlaege";
/** Story 18.10: Einkauf › Lieferantenpreise (Liste `supplier_prices`), Import/Export und Läufe. */
export const SUPPLIER_PRICES_PATH = "/einkauf/lieferantenpreise";
export const SUPPLIER_PRICES_IMPORT_PATH = "/einkauf/lieferantenpreise/import";
export const SUPPLIER_PRICES_MASS_EDIT_PATH =
  "/einkauf/lieferantenpreise/massenbearbeitung";
/** Story 10.14: Einkauf › „EK gestiegen – Marge zu niedrig“ (Liste `margin_reviews`). */
export const MARGIN_REVIEWS_PATH = "/einkauf/marge-pruefen";
/** Massenbearbeitung je Liste (Story 2.18: „In Massenbearbeitung öffnen“ aus den KI-Vorschlägen). */
export const MASS_EDIT_PATHS: Readonly<Record<string, string>> = {
  customers: CUSTOMERS_MASS_EDIT_PATH,
  articles: ARTICLES_MASS_EDIT_PATH,
  stock: INVENTORY_MASS_EDIT_PATH,
  bin_locations: BIN_LOCATIONS_MASS_EDIT_PATH,
  supplier_prices: SUPPLIER_PRICES_MASS_EDIT_PATH,
  suppliers: SUPPLIERS_MASS_EDIT_PATH,
  tasks: TASKS_MASS_EDIT_PATH,
};

/** Automatisierung (Regel-Baukasten, epic-regel-baukasten Story 1): Regeln und Protokoll. */
export const AUTOMATION_PATH = "/automatisierung";

/** Berichte (Story 18.1, 18.6): Übersicht und Berichte Tagesumsatz, Monatsumsatz, Umsatz nach Kanal. */
export const REPORTS_PATH = "/berichte";
export const DAILY_REVENUE_PATH = "/berichte/tagesumsatz";
export const MONTHLY_REVENUE_PATH = "/berichte/monatsumsatz";
export const CHANNEL_REVENUE_PATH = "/berichte/umsatz-nach-kanal";
/** Liste Verkaufte Positionen (Story 18.24). */
export const SOLD_POSITIONS_PATH = "/berichte/verkaufte-positionen";
/** Berichte › Zahlungseingänge (Story 18.27, nur Admin): Vorschau der erwarteten Zahlungseingänge. */
export const INCOMING_PAYMENTS_PATH = "/berichte/zahlungseingaenge";
/** Zeiterfassung › Heute (Story 22.1): Stempel-Karte und Tagesliste der eigenen Person. */
export const TIME_TRACKING_PATH = "/zeiterfassung";
/** Zeiterfassung › Meine Zeiten, Team (Admin, Vertreter), Arbeitszeitgesetz (nur Admin) – Story 22.4. */
export const MY_TIMES_PATH = "/zeiterfassung/meine-zeiten";
export const STAFF_TEAM_PATH = "/zeiterfassung/team";
export const WORKING_TIME_PATH = "/zeiterfassung/arbeitszeitgesetz";
/** Zeiterfassung › Nachträge (Story 22.5, nur Admin): Liste der Nachträge mit „Zurückweisen“. */
export const TIME_AMENDMENTS_PATH = "/zeiterfassung/nachtraege";
/** Zeiterfassung › Monatsübersicht (Story 22.19, nur Admin): Monat abschließen, wartende Änderungen freigeben. */
export const MONTH_CLOSE_PATH = "/zeiterfassung/monatsuebersicht";
/** Zeiterfassung › Urlaub & Abwesenheit (alle) und Genehmigen (Admin, Vertreter) – Story 22.6. */
export const LEAVE_PATH = "/zeiterfassung/urlaub";
export const APPROVALS_PATH = "/zeiterfassung/genehmigen";
/** Zeiterfassung › Kalender (Story 22.8, alle): eigener Monat und Team (`?ansicht=team&monat=2027-03`). */
export const CALENDAR_PATH = "/zeiterfassung/kalender";
/** Liste „Retouren“ (Story 14.1). */
export const RETURNS_PATH = "/retouren";
/** Fragen an die Daten (Story 18.21, Rudolf 11.10.2026: Karte unter Berichte). */
export const DATA_QUESTIONS_PATH = "/berichte/fragen";

/**
 * Seite je Liste des Listen-Baukastens (für „Als Liste öffnen“ einer Antwort, Story 18.21). Listen ohne eigene Seite
 * (z. B. Zählliste einer Inventur) fehlen; dort gibt es den Knopf nicht.
 */
export const LIST_PAGE_PATHS: Readonly<Record<string, string>> = {
  customers: CUSTOMERS_PATH,
  articles: ARTICLES_PATH,
  bin_locations: BIN_LOCATIONS_PATH,
  stock: INVENTORY_PATH,
  stock_movements: INVENTORY_MOVEMENTS_PATH,
  stock_valuation: INVENTORY_VALUATION_PATH,
  orders: ORDERS_PATH,
  order_drafts: ORDER_DRAFTS_PATH,
  quotes: QUOTES_PATH,
  order_backlog: ORDER_BACKLOG_PATH,
  blanket_orders: BLANKET_ORDERS_PATH,
  purchase_orders: PURCHASE_ORDERS_PATH,
  reorder_proposals: REORDER_PROPOSALS_PATH,
  supplier_prices: SUPPLIER_PRICES_PATH,
  stocktakes: STOCKTAKES_PATH,
  recount_marks: RECOUNT_PATH,
  sold_positions: SOLD_POSITIONS_PATH,
  // Story 18.27: Liste „Zahlungseingänge“ (nur Admin).
  incoming_payments: INCOMING_PAYMENTS_PATH,
  shipping_queue: "/versand",
  // Story Selbstabholung: Liste „Abholbereit“.
  pickup_ready: "/versand/abholung",
  // Story 11.41: Liste „Sendungen“.
  shipments: SHIPMENTS_PATH,
  margin_reviews: MARGIN_REVIEWS_PATH,
  // Story 14.1: Liste „Retouren“.
  returns: RETURNS_PATH,
};

/** Bereiche, die es noch nicht gibt (leere Seite mit einem ruhigen Satz). */
export const comingSoon: readonly NavItem[] = [
  ...mainNav.slice(2),
  footNav[0]!,
].filter(
  (item) =>
    // Story 24.1: Team-Chat hat eine eigene Seite.
    item.path !== CHAT_PATH &&
    item.path !== ORDERS_PATH &&
    // Story 9.7: Liste „Angebote“.
    item.path !== QUOTES_PATH &&
    item.path !== CUSTOMERS_PATH &&
    item.path !== ARTICLES_PATH &&
    item.path !== INVENTORY_PATH &&
    item.path !== PURCHASING_PATH &&
    item.path !== REPORTS_PATH &&
    item.path !== AUTOMATION_PATH &&
    // Story 11.1: Liste „Versand“ und Scan-Maske.
    item.path !== "/versand" &&
    // Story 11.41: Liste „Sendungen“.
    item.path !== SHIPMENTS_PATH &&
    // Ticket 1.250: Finanzen mit Übersicht und vier Bereichen.
    item.path !== FINANCE_PATH &&
    // Ticket 1.249 / Story 14.1: Liste „Retouren“ über den Listen-Baukasten.
    item.path !== RETURNS_PATH &&
    // Story 22.1: Zeiterfassung.
    item.path !== TIME_TRACKING_PATH,
);
